import React, { useEffect, useState, useContext } from "react";
import Style from "./index.module.css";
import authInstance from "../../../../instance/AuthInstance";
import { UserContext } from "../../../../Contexts/UserContext";
import Breadcrumb from "../../../../Components/Breadcrumb/Breadcrumb";
import { useLocation } from "react-router-dom";
import { FaRocket, FaClock, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { toast } from "react-toastify";

const MyAdsList = () => {
    const [ads, setAds] = useState([]);
    const [boostStats, setBoostStats] = useState({
        boostsRemaining: 0,
        maxBoosts: 0,
        usedBoosts: 0,
        planName: ''
    });
    const [loading, setLoading] = useState(true);
    const { User } = useContext(UserContext);
    const location = useLocation();

    // Always compute path segments even if empty
    const pathSegment = location.pathname
        .split('/')
        .filter((segment) => segment);

    const fetchAds = async () => {
        try {
            const response = await authInstance.get(
                `/api/user/product/get_ownadvertisements?userId=${User._id}`
            );
            const userAds = response.data?.data || [];
            setAds(userAds);
        } catch (error) {
            console.error("Error fetching your advertisements:", error);
        }
    };

    const fetchBoostStats = async () => {
        try {
            const response = await authInstance.get(
                `/api/user/product/get_boost_count/${User._id}`
            );
            if (response.data) {
                setBoostStats(response.data);
            }
        } catch (error) {
            console.error("Error fetching boost stats:", error);
        }
    };

    useEffect(() => {
        if (User?._id) {
            Promise.all([fetchAds(), fetchBoostStats()]).finally(() => setLoading(false));
        }
    }, [User?._id]);

    const handleBoost = async (adId) => {
        if (boostStats.boostsRemaining <= 0) {
            toast.error("You have used all your boosts for this month!");
            return;
        }

        try {
            const response = await authInstance.put(`/api/user/product/boost/${adId}`);

            if (response.data?.success) {
                toast.success("Advertisement boosted successfully! 🚀");
                // Update local state to reflect changes immediately
                setAds(prevAds => prevAds.map(ad =>
                    ad._id === adId
                        ? { ...ad, isBoosted: true, boostedAt: new Date() }
                        : ad
                ));
                // Refresh stats
                fetchBoostStats();
            }
        } catch (error) {
            console.error("Error boosting product:", error);
            toast.error(error.response?.data?.message || "Failed to boost advertisement");
        }
    };

    const getBoostTimeAgo = (dateString) => {
        if (!dateString) return "";
        const now = new Date();
        const boostedDate = new Date(dateString);
        const diffInSeconds = Math.floor((now - boostedDate) / 1000);

        if (diffInSeconds < 60) return "Just now";
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        return `${Math.floor(diffInSeconds / 86400)}d ago`;
    };

    return (
        <div className={Style.myAdsContainer}>
            <div className={Style.headerRow}>
                <h2 className={Style.title}>My Advertisements</h2>

                {/* Boost Counter Badge */}
                {!loading && boostStats.planName !== 'Free Plan' && (
                    <div className={Style.boostCounter}>
                        <div className={Style.couterIcon}>
                            <FaRocket />
                        </div>
                        <div className={Style.counterInfo}>
                            <span className={Style.counterLabel}>{boostStats.planName}</span>
                            <span className={Style.counterValue}>
                                {boostStats.boostsRemaining} / {boostStats.maxBoosts} Boosts Available
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* Breadcrumb always rendered */}
            <Breadcrumb customName="My Advertisements" pathSegments={pathSegment} />

            {loading ? (
                <div className={Style.loader}>Loading...</div>
            ) : ads.length === 0 ? (
                <p className={Style.noAds}>No advertisements found.</p>
            ) : (
                <div className={Style.adsList}>
                    {ads.map((ad) => {
                        const createdDate = new Date(ad.createdAt).toLocaleDateString();
                        const expiryDate = new Date(ad.expiryDate).toLocaleDateString();
                        const isActive = new Date(ad.expiryDate) >= new Date();

                        return (
                            <div key={ad._id} className={`${Style.adCard} ${(ad.isBoosted || ad.boosted) ? Style.boostedCard : ''}`}>
                                <div className={Style.adContent}>
                                    <img
                                        src={ad.images?.[0]?.secure_url}
                                        alt={ad.title}
                                        className={Style.image}
                                    />
                                    <div className={Style.details}>
                                        <div className={Style.titleRow}>
                                            <h3>{ad.title}</h3>
                                            {(ad.isBoosted || ad.boosted) && (
                                                <span className={Style.boostBadge}>
                                                    <FaRocket /> Recommended
                                                </span>
                                            )}
                                        </div>
                                        <p><strong>Created:</strong> {createdDate}</p>
                                        <p><strong>Expires:</strong> {expiryDate}</p>
                                        <div className={Style.statusRow}>
                                            <p className={`${Style.status} ${isActive ? Style.active : Style.expired}`}>
                                                <span className={Style.dot}></span>
                                                {isActive ? "Active" : "Expired"}
                                            </p>

                                            {(ad.isBoosted || ad.boosted) && (
                                                <span className={Style.boostTime}>
                                                    <FaClock /> Recommended {getBoostTimeAgo(ad.boostedAt)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className={Style.adActions}>
                                    {(ad.isBoosted || ad.boosted) ? (
                                        <div className={Style.boostStatusSuccess}>
                                            <FaCheckCircle /> Ad is Recommended
                                        </div>
                                    ) : !isActive ? (
                                        <div className={Style.boostStatusExpired}>
                                            Cannot Boost (Expired)
                                        </div>
                                    ) : boostStats.boostsRemaining === 0 ? (
                                        <div className={Style.boostStatusEmpty}>
                                            <FaExclamationCircle /> 0 Boosts Left - Upgrade Plan
                                        </div>
                                    ) : (
                                        <button
                                            className={Style.boostButton}
                                            onClick={() => handleBoost(ad._id)}
                                        >
                                            <FaRocket /> Boost Ad
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Boost Statistics Section */}
            {!loading && boostStats.planName !== 'Free Plan' && boostStats.maxBoosts > 0 && (
                <div className={Style.statsSection}>
                    <h3>📊 Boost Statistics</h3>
                    <div className={Style.statsGrid}>
                        <div className={Style.statItem}>
                            <span className={Style.statLabel}>Used This Month</span>
                            <span className={Style.statValue}>{boostStats.usedBoosts}</span>
                        </div>
                        <div className={Style.statItem}>
                            <span className={Style.statLabel}>Remaining</span>
                            <span className={`${Style.statValue} ${Style.highlight}`}>
                                {boostStats.boostsRemaining}
                            </span>
                        </div>
                        <div className={Style.statItem}>
                            <span className={Style.statLabel}>Total / Month</span>
                            <span className={Style.statValue}>{boostStats.maxBoosts}</span>
                        </div>
                    </div>
                    <p className={Style.tipText}>
                        💡 Tip: Recommended ads appear at the top of search results and category pages!
                    </p>
                </div>
            )}
        </div>
    );
};

export default MyAdsList;
